'use client';

import {
  BrowsersIcon,
  CubeIcon,
  BuildingsIcon,
  CaretUpDownIcon,
  FileTextIcon,
  FlagIcon,
  LinkIcon,
  SignOutIcon,
  EnvelopeIcon,
  NoteIcon,
  UserIcon,
  FlowArrowIcon,
  HeadCircuitIcon,
} from '@phosphor-icons/react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { AuthContext } from '@/context/auth.context';
import { useContext } from 'react';
import { useNavigate } from '@tanstack/react-router';
import paths from '@/paths';
import CurrentUser from 'src/types/CurrentUser';
import { isAdmin } from '@/utils/user';
import { useViewMode } from '@/context/view-mode.context';
import { useFeatureFlag } from '@/integrations/feature-flags/feature-flags';

function LabelledUserAvatar({
  currentUser,
}: {
  currentUser: CurrentUser | undefined;
}) {
  return (
    <>
      <Avatar className="h-8 w-8 rounded-lg">
        <AvatarImage src={undefined} alt={currentUser?.name} />
        <AvatarFallback className="rounded-lg">
          <UserIcon />
        </AvatarFallback>
      </Avatar>
      <div className="grid flex-1 text-left text-sm leading-tight">
        <span className="truncate font-medium">{currentUser?.name}</span>
        <span className="truncate text-xs">{currentUser?.emails[0]}</span>
      </div>
    </>
  );
}

export function NavUser() {
  const { isMobile } = useSidebar();
  const { currentUser, logOut } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const navigate = useNavigate();
  const { enabled: isMemoryBankNotesEnabled } = useFeatureFlag(
    'memory-bank-notes-enabled',
    false,
  );

  const isExternalShareUser = currentUser?.isExternalShareUser ?? false;

  const handleLogout = async () => {
    try {
      await logOut();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <LabelledUserAvatar currentUser={currentUser} />
              <CaretUpDownIcon className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <LabelledUserAvatar currentUser={currentUser} />
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {!isExternalShareUser && (
                <>
                  <DropdownMenuItem
                    onClick={() =>
                      navigate({
                        to: paths.authenticated.settings.organization,
                      })
                    }
                  >
                    <BuildingsIcon />
                    Manage Organization
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      navigate({ to: paths.authenticated.documents })
                    }
                  >
                    <FileTextIcon />
                    Documents
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      navigate({ to: paths.authenticated.inboundEmails })
                    }
                  >
                    <EnvelopeIcon />
                    Inbound Emails
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuItem
                onClick={() =>
                  navigate({ to: paths.authenticated.sharedLinks })
                }
              >
                <LinkIcon />
                Shared Links
              </DropdownMenuItem>
              {!isExternalShareUser && isMemoryBankNotesEnabled && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({ to: paths.authenticated.memoryBankNotes })
                  }
                >
                  <NoteIcon />
                  Memory Bank Notes
                </DropdownMenuItem>
              )}
              {!isExternalShareUser && isAdmin(currentUser, viewMode) && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({ to: paths.authenticated.integrations.list })
                  }
                >
                  <CubeIcon />
                  Integrations
                </DropdownMenuItem>
              )}
              {!isExternalShareUser && isAdmin(currentUser, viewMode) && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({ to: paths.authenticated.workflows.list })
                  }
                >
                  <FlowArrowIcon />
                  Workflows
                </DropdownMenuItem>
              )}
              {!isExternalShareUser && isAdmin(currentUser, viewMode) && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({ to: paths.authenticated.agents.list })
                  }
                >
                  <HeadCircuitIcon />
                  Agents
                </DropdownMenuItem>
              )}
              {!isExternalShareUser && isAdmin(currentUser, viewMode) && (
                <DropdownMenuItem
                  onClick={() => navigate({ to: paths.authenticated.portals })}
                >
                  <BrowsersIcon />
                  Portals
                </DropdownMenuItem>
              )}
              {!isExternalShareUser && isAdmin(currentUser, viewMode) && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({ to: paths.authenticated.featureFlags })
                  }
                >
                  <FlagIcon />
                  Feature Flags
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <SignOutIcon />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
